import type { Surcharge, SurchargeRate } from '@api/useFreight/types'
import dayjs from 'dayjs'

/**
 * Formats a surcharge rate value for display
 * @param value The numerical rate value
 * @param type The surcharge type ('Percentage' or 'Per Mile')
 * @returns Formatted rate string
 */
export const formatSurchargeRate = (value: number | null | undefined, type = 'Percentage'): string => {
  if (value === null || value === undefined) return 'N/A'

  return type === 'Percentage' ? `${value.toFixed(3)}%` : `$${value.toFixed(5)}`
}

/**
 * Converts a displayed surcharge rate to a decimal for API storage
 * @param displayRate The rate as shown in the UI
 * @param isPercentage Whether this is a percentage rate
 * @returns The rate value for API storage
 */
export const convertRateForApi = (displayRate: number, isPercentage: boolean): number => {
  return isPercentage ? displayRate / 100 : displayRate
}

/**
 * Converts an API surcharge rate to display format
 * @param apiRate The rate from the API
 * @param isPercentage Whether this is a percentage rate
 * @returns The rate value for display
 */
export const convertRateForDisplay = (apiRate: number, isPercentage: boolean): number => {
  return isPercentage ? apiRate * 100 : apiRate
}

/**
 * Gets the most recent rate for a surcharge
 * @param surcharge The surcharge object
 * @returns The most recent rate, or null if none
 */
export const getLatestRate = (surcharge: Surcharge): SurchargeRate | null => {
  if (!surcharge.rates || surcharge.rates.length === 0) return null

  return [...surcharge.rates].sort(
    (a, b) => dayjs(b.effective_from as string).valueOf() - dayjs(a.effective_from as string).valueOf()
  )[0]
}

/**
 * Determines if a surcharge rate is a percentage type
 * @param surcharge The surcharge object
 * @returns boolean indicating if it's a percentage type
 */
export const isPercentageType = (surcharge: Surcharge): boolean => {
  return surcharge.type === 'Percentage'
}
export type SurchargeType = 'Percentage' | 'Per Mile'

export function formatSurchargeValue(value: number, type: 'Percentage' | 'Per Mile'): string {
  if (value === null || value === undefined) return 'N/A'

  if (type === 'Percentage') {
    // Convert decimal to percentage and format with 5 decimal places
    return `${(value * 100).toFixed(3)}%`
  }
  // Format with 5 decimal places for Per Mile
  return `$${value.toFixed(5)}`
}
